import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faFileContract, faDownload, faHandshake, faPen } from '@fortawesome/pro-solid-svg-icons';
import { RelationshipTypeService } from '../../../../core/services/relationship-type.service';
import { RelationshipType, RelationshipTypeDefault } from '../../../../core/models/relationship-type.model';
import { PopupInfoService } from '../../../../shared/services/popup-info.service';
import { EditRelationshipDefaultModal } from '../components/edit-relationship-default-modal/edit-relationship-default-modal';

@Component({
  selector: 'app-relationship-types-list-page',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, EditRelationshipDefaultModal],
  templateUrl: './relationship-types-list.html',
  styleUrls: ['./relationship-types-list.scss'],
})
export class RelationshipTypesListPage implements OnInit {
  private readonly relationshipTypeService = inject(RelationshipTypeService);
  private readonly popupInfoService = inject(PopupInfoService);

  readonly faFileContract = faFileContract;
  readonly faDownload = faDownload;
  readonly faHandshake = faHandshake;
  readonly faPen = faPen;

  readonly isLoading = signal(true);

  relationshipTypes: RelationshipType[] = [];
  downloadingType: string | null = null;
  editingDefault: { rt: RelationshipType; def: RelationshipTypeDefault } | null = null;

  readonly relationshipTypeDescriptions: Record<string, string> = {
    Referenciador:
      'Únicamente identifica oportunidades y las deriva a SDi. Puede optar a una comisión del 10% del proyecto, pero nunca participa en el negocio recurrente.',
    Distribuidor:
      'Comercializa productos o servicios de SDi, pudiendo o no participar en la implantación. Puede participar en el negocio recurrente.',
    'Partner Estratégico':
      'Alianzas estratégicas que aportan valor al negocio, aunque no necesariamente exista una venta directa.',
    Colaborador:
      'Empresas o profesionales con los que SDi mantiene una relación de colaboración no remunerada, pudiendo compartir conocimiento, oportunidades, acciones, eventos o iniciativas cuando exista interés mutuo.',
  };

  readonly distributionModalityDescriptions: Record<string, string> = {
    'Distribución Comercial':
      'Únicamente comercializas. SDi factura al cliente final, y realiza la implantación y el soporte.',
    Reventa: 'Compras a SDi y facturas al cliente final. SDi realiza la implantación y el soporte.',
    'Modelo Mixto':
      'La contratación, implantación y soporte se acuerdan según la operación o el producto concreto.',
    'Distribución Integral':
      'Compras a SDi, facturas al cliente final y te encargas también de la implantación y el soporte.',
  };

  private readonly distributionModalityOrder = [
    'Distribución Comercial',
    'Reventa',
    'Modelo Mixto',
    'Distribución Integral',
  ];

  get collaboratorType(): RelationshipType | undefined {
    return this.relationshipTypes.find((rt) => rt.type === 'Colaborador');
  }

  get simpleTypes(): RelationshipType[] {
    return this.relationshipTypes.filter(
      (rt) => rt.defaults.length <= 1 && rt.type !== 'Colaborador',
    );
  }

  get complexTypes(): RelationshipType[] {
    return this.relationshipTypes.filter((rt) => rt.defaults.length > 1);
  }

  sortedDefaults(rt: RelationshipType): RelationshipTypeDefault[] {
    return [...rt.defaults].sort((a, b) => {
      const indexA = this.distributionModalityOrder.indexOf(a.distribution_modality || '');
      const indexB = this.distributionModalityOrder.indexOf(b.distribution_modality || '');
      return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
    });
  }

  ngOnInit(): void {
    this.loadRelationshipTypes();
  }

  loadRelationshipTypes(): void {
    this.isLoading.set(true);
    this.relationshipTypeService.getRelationshipTypes().subscribe({
      next: (response) => {
        this.relationshipTypes = response.data;
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.popupInfoService.showError('Error al cargar los modelos de relación');
      },
    });
  }

  downloadTemplate(rt: RelationshipType): void {
    if (!rt.contract_template || this.downloadingType) return;

    this.downloadingType = rt.type;
    this.relationshipTypeService.downloadTemplate(rt.type).subscribe({
      next: (blob) => {
        this.triggerDownload(blob, `Plantilla-${rt.type}.pdf`);
        this.downloadingType = null;
      },
      error: () => {
        this.downloadingType = null;
        this.popupInfoService.showError('Error al descargar la plantilla');
      },
    });
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  formatPercent(value: string | undefined | null): string {
    if (value === null || value === undefined) return '-';
    return `${Number(value)}%`;
  }

  formatCurrency(value: string | undefined | null): string {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(Number(value));
  }

  formatNumber(value: string | undefined | null): string {
    if (value === null || value === undefined) return '-';
    return `${Number(value)}`;
  }

  openEditModal(rt: RelationshipType, def: RelationshipTypeDefault): void {
    this.editingDefault = { rt, def };
  }

  closeEditModal(): void {
    this.editingDefault = null;
  }

  onEditSuccess(updated: RelationshipTypeDefault): void {
    if (!this.editingDefault) return;

    const { rt, def } = this.editingDefault;
    const index = rt.defaults.findIndex((d) => d.id === def.id);
    if (index !== -1) {
      rt.defaults[index] = updated;
    }
    this.closeEditModal();
  }
}
